import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import type { RowSelectionState } from '@tanstack/react-table'
import type { AlgoliaHit } from 'instantsearch.js'

import { useActiveUser } from 'Hooks'

import type { SearchResultsDocument } from 'Types/globalSearch/searchResultsDocument.types'

import { resolveSubmitColor } from './resolveSubmitColor'
import type { TagAutocompleteHandle } from './TagAutocomplete.types'
import type { Tag } from './tagging.types'
import type { TaggingTableContextType } from './TaggingProvider.types'
import { INITIAL_STATE, taggingReducer } from './taggingReducer'
import { useOrgTags } from './useOrgTags'
import { useRentalTagMutationActions } from './useRentalTagMutationActions'
import { useTagDispatchWrappers } from './useTagDispatchWrappers'
import { useTagKeyboardHandlers } from './useTagKeyboardHandlers'
import { useTagOutsideClickDismiss } from './useTagOutsideClickDismiss'

interface UseTaggingTableOptions {
  hits: AlgoliaHit<SearchResultsDocument>[]
  refresh: () => void
}

export const useTaggingTable = (
  options: UseTaggingTableOptions,
): TaggingTableContextType => {
  const { hits, refresh } = options
  const { activeOrganisation } = useActiveUser()
  const orgId = String(activeOrganisation?.id ?? '')

  const [state, dispatch] = useReducer(taggingReducer, INITIAL_STATE)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [syncAction, setSyncAction] = useState<'add' | 'remove' | null>(null)

  const { orgTags, orgTagsRef, refetchTags } = useOrgTags(orgId)

  const autocompleteRef = useRef<TagAutocompleteHandle | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const handleSubmitRef = useRef<() => void>(() => {})
  const justEnteredEditRef = useRef(false)
  const pendingRefreshRef = useRef(false)
  const prevModeRef = useRef(state.mode)
  const prevSelectionRef = useRef<RowSelectionState>(rowSelection)
  const hitsRef = useRef(hits)

  hitsRef.current = hits

  const {
    enterEditMode,
    exitEditMode,
    setActiveEditorRowId,
    setInputText,
    setSelectedColor,
  } = useTagDispatchWrappers(dispatch)

  const getRentalIdsWithTag = useCallback((rentalIds: string[], tag: Tag) => {
    return rentalIds.filter(rentalId => {
      const hit = hitsRef.current.find(h => String(h.id) === rentalId)
      const rowTags = hit?.['rental.tags'] ?? []

      return rowTags.some(t => String(t.id) === tag.id)
    })
  }, [])

  const completeTagMutation = useCallback(async () => {
    pendingRefreshRef.current = true
    refresh()

    await refetchTags()
  }, [refetchTags, refresh])

  const handleAddCompleted = useCallback(() => {
    setSyncAction('add')

    return completeTagMutation()
  }, [completeTagMutation])

  const handleRemoveCompleted = useCallback(() => {
    setSyncAction('remove')

    return completeTagMutation()
  }, [completeTagMutation])

  const { addTag, isAddingTag, isRemovingTag, removeTag } =
    useRentalTagMutationActions({
      getRentalIdsWithTag,
      onAddCompleted: handleAddCompleted,
      onRemoveCompleted: handleRemoveCompleted,
      orgId,
      orgTagsRef,
    })

  const isEditorOpen = state.mode === 'edit' && state.activeEditorRowId !== null
  const isMutating = isAddingTag || isRemovingTag || !!syncAction

  // Guards external callers (TanStack/MobX); internal effects use setRowSelection directly
  const guardedSetRowSelection: typeof setRowSelection = useCallback(
    updater => {
      if (isMutating) {
        return
      }

      setRowSelection(updater)
    },
    [isMutating],
  )

  // --- Derived selection state ---

  const selectedRowIds = useMemo(
    () => Object.keys(rowSelection).filter(id => rowSelection[id]),
    [rowSelection],
  )

  const selectedCount = selectedRowIds.length

  const isRowSelected = useCallback(
    (rowId: string) => !!rowSelection[rowId],
    [rowSelection],
  )

  // --- Selection sync effects ---

  // Effect 1: auto-check triggerRowId when entering edit mode
  useEffect(() => {
    if (state.mode === 'edit' && state.triggerRowId) {
      justEnteredEditRef.current = true
      setRowSelection(prev => ({ ...prev, [state.triggerRowId!]: true }))
    }
  }, [state.mode, state.triggerRowId])

  // Effect 2: clear selection on edit -> read transition
  useEffect(() => {
    if (prevModeRef.current === 'edit' && state.mode === 'read') {
      setRowSelection({})
    }

    prevModeRef.current = state.mode
  }, [state.mode])

  // Effect 3: uncheck-all guard - exit edit mode if no rows selected
  useEffect(() => {
    if (justEnteredEditRef.current) {
      justEnteredEditRef.current = false

      return
    }

    const hasSelectedRows = Object.values(rowSelection).some(Boolean)

    if (state.mode === 'edit' && !hasSelectedRows) {
      exitEditMode()
    }
  }, [rowSelection, state.mode, exitEditMode])

  // Effect 4: reassign editor row when active row is unchecked
  useEffect(() => {
    if (state.mode !== 'edit' || !state.activeEditorRowId) {
      prevSelectionRef.current = rowSelection

      return
    }

    const wasSelected = prevSelectionRef.current[state.activeEditorRowId]
    const isSelected = rowSelection[state.activeEditorRowId]

    if (wasSelected && !isSelected) {
      const currentSelectedIds = Object.keys(rowSelection).filter(
        id => rowSelection[id],
      )

      if (currentSelectedIds.length > 0) {
        const rowOrder = hits.map(h => String(h.id))
        const currentIndex = rowOrder.indexOf(state.activeEditorRowId)
        let nextRowId: string | null = null

        for (let i = currentIndex + 1; i < rowOrder.length; i += 1) {
          if (currentSelectedIds.includes(rowOrder[i])) {
            nextRowId = rowOrder[i]
            break
          }
        }

        if (!nextRowId) {
          for (let i = 0; i < currentIndex; i += 1) {
            if (currentSelectedIds.includes(rowOrder[i])) {
              nextRowId = rowOrder[i]
              break
            }
          }
        }

        if (nextRowId) {
          setActiveEditorRowId(nextRowId)
        }
      }
    }

    prevSelectionRef.current = rowSelection
  }, [
    rowSelection,
    state.mode,
    state.activeEditorRowId,
    setActiveEditorRowId,
    hits,
  ])

  // Effect 5: complete sync after refresh brings fresh hits
  useEffect(() => {
    if (!syncAction || !pendingRefreshRef.current) {
      return
    }

    pendingRefreshRef.current = false

    if (syncAction === 'add') {
      dispatch({ type: 'EXIT_EDIT_MODE' })
    }

    setSyncAction(null)
  }, [hits]) // eslint-disable-line react-hooks/exhaustive-deps

  // --- Actions ---

  const canSubmit = state.inputText.trim().length > 0

  const handleSubmit = useCallback(() => {
    if (!canSubmit || isAddingTag) {
      return
    }

    const trimmed = state.inputText.trim()
    const color = resolveSubmitColor(
      state.inputText,
      state.selectedColor,
      orgTags,
    )

    addTag(selectedRowIds, trimmed, color)
  }, [
    canSubmit,
    isAddingTag,
    orgTags,
    selectedRowIds,
    state.inputText,
    state.selectedColor,
    addTag,
  ])

  handleSubmitRef.current = handleSubmit

  // --- Keyboard handler ---

  useTagKeyboardHandlers({
    autocompleteRef,
    exitEditMode,
    handleSubmitRef,
    inputRef,
    inputText: state.inputText,
    isMutating: isAddingTag || isRemovingTag,
    mode: state.mode,
  })

  // --- Outside click dismiss (when input is empty) ---

  useTagOutsideClickDismiss({
    exitEditMode,
    inputText: state.inputText,
    isMutating: isAddingTag || isRemovingTag,
    mode: state.mode,
    scopeSelector: '.TaggingTable .Table-row',
  })

  return {
    ...state,
    addTag,
    autocompleteRef,
    canSubmit,
    enterEditMode,
    exitEditMode,
    handleSubmit,
    inputRef,
    isAddingTag,
    isEditorOpen,
    isRemovingTag,
    isRowSelected,
    orgTags,
    removeTag,
    rowSelection,
    selectedCount,
    selectedRowIds,
    setActiveEditorRowId,
    setInputText,
    setRowSelection: guardedSetRowSelection,
    setSelectedColor,
  }
}
